import { Feature } from "ol";
import { click } from "ol/events/condition";
import { Geometry } from "ol/geom";
import Select, { SelectEvent } from "ol/interaction/Select";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useContext, useEffect, useMemo } from "react";
import MapContext from "../components/Map/MapContext";

export default function useSelectInteraction(
  handleSelect?: (features: Feature<any>[]) => void,
  layerName?: string,
  sourceLayer?: VectorLayer<VectorSource<Feature<Geometry>>>
) {
  const { map } = useContext(MapContext);

  const layer = useMemo(
    () =>
      sourceLayer ||
      (layerName &&
        (map
          ?.getLayers()
          ?.getArray()
          ?.find((l) => l.getProperties().name === layerName) as VectorLayer<
          VectorSource<Feature<Geometry>>
        >)),
    [map, sourceLayer, layerName]
  );

  const selectClick = useMemo(() => {
    // Configure a new interaction that lets user select fields by click
    return (
      layer &&
      new Select({
        condition: click,
      })
    );
  }, [layer]);

  useEffect(() => {
    function onSelect(e: SelectEvent) {
      try {
        handleSelect && handleSelect(e?.selected);
        (selectClick as Select).getFeatures()?.clear();
        return true;
      } catch (e) {}
    }

    function handleMapClick(e: any) {
      let features: any[] = [];
      try {
        map?.forEachFeatureAtPixel(e.pixel, (f, l) => {
          if (l && l === layer) features.push(layer);
        });
      } catch (e) {
        console.warn(e);
      } finally {
        if (!features.length) {
          handleSelect && handleSelect([]);
        }
        return true;
      }
    }

    selectClick && selectClick?.on("select", onSelect);
    selectClick && map?.addInteraction(selectClick);
    map?.on("click", handleMapClick);
    return () => {
      selectClick && selectClick?.un("select", onSelect);
      selectClick && map?.removeInteraction(selectClick);
      map?.un("click", handleMapClick);
    };
  }, [map, layer, handleSelect, selectClick]);
}
